import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	input,
	InputSignal,
	model,
	ModelSignal,
	OnInit,
	Signal,
	signal,
	WritableSignal
} from "@angular/core";
import { IContact } from "@calaosoft/osapp-common/contacts/models/IContact";
import { IGroupMember } from "@calaosoft/osapp-common/contacts/models/IGroupMember";
import { Contact } from "@calaosoft/osapp-common/contacts/models/contact";
import { IConversationActivity } from "@calaosoft/osapp-common/conversations/models/iconversation-activity";
import { IParticipant } from "@calaosoft/osapp-common/conversations/models/iparticipant";
import { DateHelper } from "@calaosoft/osapp-common/dates/helpers/dateHelper";
import { IEntity } from "@calaosoft/osapp-common/entities/models/ientity";
import { EInteropSystem } from "@calaosoft/osapp-common/interoperabilite/models/einterop-system";
import { ObserveArray } from "@calaosoft/osapp-common/observable/decorators/observe-array.decorator";
import { ObservableArray } from "@calaosoft/osapp-common/observable/models/observable-array";
import { EPermissionScopes } from "@calaosoft/osapp-common/permissions/models/epermission-scopes";
import { Queue } from "@calaosoft/osapp-common/queue/decorators/queue.decorator";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { StringHelper } from "@calaosoft/osapp-common/utils/helpers/stringHelper";
import { Keyboard } from "@capacitor/keyboard";
import { IonItemSliding } from "@ionic/angular";
import { EMPTY, Observable } from "rxjs";
import { catchError, filter, finalize, mergeMap, takeUntil, tap } from "rxjs/operators";
import { ComponentBase } from "../../../helpers/ComponentBase";
import { EConversationStatus } from "../../../model/conversation/EConversationStatus";
import { IConversation } from "../../../model/conversation/IConversation";
import { IConversationListParams } from "../../../model/conversation/IConversationListParams";
import { IOpenConversationOptions } from "../../../model/conversation/IOpenConversationOptions";
import { EConversationType } from "../../../model/conversation/e-conversation-type";
import { ELinkAction } from "../../../model/link/ELinkAction";
import { ELinkTemplate } from "../../../model/link/ELinkTemplate";
import { LinkInfo } from "../../../model/link/LinkInfo";
import { IPopoverItemParams } from "../../../model/popover/IPopoverItemParams";
import { IUiResponse } from "../../../model/uiMessage/IUiResponse";
import { IConversationListItem } from "../../../modules/conversations/model/iconversation-list-item";
import { EModalSize } from "../../../modules/modal";
import { ModalService } from "../../../modules/modal/services/modal.service";
import { HasPermissions } from "../../../modules/permissions/decorators/has-permissions.decorator";
import { IHasPermission, PermissionsService } from "../../../modules/permissions/services/permissions.service";
import { ArchivingService } from "../../../services/archiving.service";
import { ContactsService } from "../../../services/contacts.service";
import { ConversationService } from "../../../services/conversation.service";
import { ShowMessageParamsPopup } from "../../../services/interfaces/ShowMessageParamsPopup";
import { PatternResolverService } from "../../../services/pattern-resolver.service";
import { PopoverService } from "../../../services/popover.service";
import { UiMessageService } from "../../../services/uiMessage.service";
import { InteropModalComponent } from "../../interoperabilite/interopModal/interop-modal.component";
import { LinkPopoverComponent } from "../../popover/linkPopover.component";

@Component({
	selector: "calao-conversation-list",
	templateUrl: "./conversations-list.component.html",
	styleUrls: ["./conversations-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ConversationsListComponent extends ComponentBase implements OnInit, IHasPermission {
	//#region FIELDS

	private static readonly C_LOG_ID = "CONVLIST.C::";

	/** Objet itemSliding qui est actif.
	 * @deprecated Cette valeur n'est pas toujours à jour.
	 * Les items peuvent être fermés par Ionic. Cette variable est mise à jour uniquement si notre code modifie l'item actif. */
	private moActiveItemSliding?: IonItemSliding;

	//#endregion

	//#region PROPERTIES

	public readonly params: InputSignal<IConversationListParams | undefined | null> = input();
	/**	Map des dates d'ajout de l'utilisateur dans un groupe par l'id du groupe. */
	public readonly userJoinDateByGroupId: InputSignal<Map<string, Date>> = input(new Map<string, Date>());

	public readonly userContactPath?: string;
	public readonly userContactId: string = ContactsService.getUserContactId();

	@HasPermissions({ permission: "delete" })
	/** Indique si l'utilisateur peut supprimer des conversations. */
	public get canDelete(): boolean {
		return true;
	}

	@HasPermissions({
		permission: EInteropSystem.microsoft,
		permissionScopes: [EPermissionScopes.conversations, EPermissionScopes.interop, EPermissionScopes.mailbox]
	})
	/** Indique si l'utilisateur a accès a une interop mail Microsoft. */
	public get canInteropMicrosoftMailbox(): boolean {
		return true;
	}

	@HasPermissions({
		permission: EInteropSystem.google,
		permissionScopes: [EPermissionScopes.conversations, EPermissionScopes.interop, EPermissionScopes.mailbox]
	})
	/** Indique si l'utilisateur a accès a une interop mail Google. */
	public get canInteropGoogleMailbox(): boolean {
		return true;
	}

	public get canInteropMailbox(): boolean {
		return this.canInteropMicrosoftMailbox || this.canInteropGoogleMailbox;
	}

	public readonly permissionScope: EPermissionScopes = EPermissionScopes.conversations;
	public readonly items: InputSignal<IConversationListItem[]> = input<IConversationListItem[]>([]);

	/** Permet de savoir si le bouton de suppression doit être affiché */
	public enableDelete = false;
	/** Valeur à passer à la barre de recherche. */
	public readonly searchValue: WritableSignal<string> = signal("");

	public conversationTypeEnabled: EConversationType[] = [];
	public conversationTypeFilteredSignal: WritableSignal<EConversationType[]> = signal([]);

	/** Conversations filtrées. */
	public readonly filteredItems: Signal<IConversationListItem[]> = computed(() => this.filterConversations());

	/** Affichage ou non du bouton de création. */
	public readonly hideFabButtonInput: ModelSignal<boolean> = model(false);
	public readonly hideFabButton: Signal<boolean> = computed(() => coerceBooleanProperty(this.hideFabButtonInput()));

	public readonly conversationsStatusFilter: InputSignal<EConversationStatus | undefined> =
		input<EConversationStatus>();

	/** Tableau des options avancées à afficher dans les popovers. */
	public advancedOptions?: IPopoverItemParams[];
	@ObserveArray<ConversationsListComponent>("advancedOptions")
	public readonly observableAdvancedOptions = new ObservableArray<IPopoverItemParams>(this.getAdvancedOptions());

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcConversation: ConversationService,
		private readonly isvcUiMessage: UiMessageService,
		private readonly isvcContact: ContactsService,
		private readonly isvcPatternResolver: PatternResolverService,
		public readonly isvcPermissions: PermissionsService,
		private readonly isvcArchiving: ArchivingService,
		private readonly isvcPopover: PopoverService,
		private readonly isvcModal: ModalService,
		poChangeDetectorRef: ChangeDetectorRef
	) {
		super(poChangeDetectorRef);

		this.userContactPath = this.isvcConversation.getCurrentUserContactPath();
		effect(() => {
			this.initConversationTypeEnabled();
		});
	}

	public ngOnInit(): void {
		this.setLinkedEntities();
	}

	private filterConversations(): IConversationListItem[] {
		let laFilteredItems: IConversationListItem[] = this.items();

		if (StringHelper.isValid(this.searchValue())) {
			laFilteredItems = laFilteredItems.filter((poItem: IConversationListItem) =>
				this.filterConversation(poItem.conversation)
			);
		}

		laFilteredItems = this.filterConversationByStatus(laFilteredItems);

		return this.orderConversationsByDate(this.filterConversationType(laFilteredItems));
	}

	/** Remplis le tableau `this.params()?.linkedEntities` en resolvant les patterns de `this.params()?.linkedEntitesPattern`. */
	private setLinkedEntities(): void {
		const loParams: IConversationListParams | undefined | null = this.params();
		if (loParams && ArrayHelper.hasElements(loParams.linkedEntities)) {
			for (let i = 0; i < loParams.linkedEntities.length; i++) {
				if (typeof loParams.linkedEntities[i] === "string") {
					// On résoud l'élément.
					const loResolvedEntity: IEntity = this.isvcPatternResolver.resolveFormsPattern<IEntity>(
						loParams.linkedEntities[i] as string
					);

					// Si résolution correct, on l'ajoute au tableau des éléments résolus.
					if (loResolvedEntity) loParams.linkedEntities[i] = loResolvedEntity;
					else
						console.error(
							`${ConversationsListComponent.C_LOG_ID}Entity pattern not resolved correctly : `,
							loParams.linkedEntities[i]
						);
				}
			}
		}
	}

	/** Ferme les options révélées de l'itemSliding. */
	private closeItemSlidingOptions(): void {
		if (this.moActiveItemSliding) {
			this.moActiveItemSliding.close();
			this.moActiveItemSliding = undefined;
		} else {
			console.warn(`${ConversationsListComponent.C_LOG_ID}Can not close active item : no active item.`);
		}
	}

	/** Exécute la suppression de la conversation après clic du bouton de confirmation.
	 * @param poConversation IConversation à supprimer.
	 */
	private execDelete(poConversation: IConversation): Observable<boolean> {
		return this.isvcConversation.delete(poConversation).pipe(tap((_) => this.closeItemSlidingOptions()));
	}

	public onConversationDelete(poConversation: IConversation): void {
		this.isvcConversation
			.isDeletable(poConversation)
			.pipe(
				mergeMap((pbCanDelete: boolean) => (pbCanDelete ? this.showDeleteConversationPopup() : EMPTY)),
				filter((poResponse: IUiResponse<boolean>) => !!poResponse.response),
				mergeMap((_) => this.execDelete(poConversation)),
				catchError((poError) => {
					console.error(ConversationsListComponent.C_LOG_ID, poError);
					return EMPTY;
				}),
				finalize(() => this.closeItemSlidingOptions()),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	private showDeleteConversationPopup(): Observable<IUiResponse<boolean>> {
		return this.isvcUiMessage.showPopup$<boolean>(
			new ShowMessageParamsPopup({
				message: "Cette conversation sera supprimée pour TOUS les utilisateurs ! <br/> Voulez-vous continuer ?",
				header: "Suppression",
				backdropDismiss: false,
				buttons: [
					{ text: "Annuler", handler: () => UiMessageService.getFalsyResponse() },
					{ text: "Continuer", handler: () => UiMessageService.getTruthyResponse() }
				]
			})
		);
	}

	/** Permet de passer à une page de conversation (vue ou création).
	 * @param poConversation IConversation cliquée, si non renseigné alors la conversation sera en mode création.
	 */
	public onItemClicked(poConversation: IConversation): void {
		Keyboard.hide().catch(() => {});
		this.isvcConversation.routeToConversationFromConversationAsync(poConversation);
	}

	/** Événement levé lors d'un swipe qui permet de fermer les items qui sont ouverts pour qu'un seul ne le soit en même temps.
	 * @param poItemSliding Liste des items pouvant être glissés pour révéler des boutons.
	 */
	public onSwipe(poItemSliding: IonItemSliding): void {
		this.openOrCloseItemSliding(poItemSliding);
	}

	@Queue<
		ConversationsListComponent,
		Parameters<ConversationsListComponent["createConversation$"]>,
		ReturnType<ConversationsListComponent["createConversation$"]>
	>({
		excludePendings: true
	})
	/** Crée et retourne une nouvelle conversation. */
	private createConversation$(): Observable<IConversation | undefined> {
		this.disableFabButton();

		return this.isvcConversation.createOrOpenConversation(this.userContactId, this.createOpenConvOptions()).pipe(
			tap({
				error: (poError) => console.error(ConversationsListComponent.C_LOG_ID, poError)
			}),
			takeUntil(this.destroyed$),
			finalize(() => this.enableFabButton())
		);
	}

	/** Supprime et recrée la toolbar en ajoutant le fab button de création de conversations désactivé. */
	private disableFabButton(): void {
		this.hideFabButtonInput.set(true);
	}

	/** Supprime et recrée la toolbar en ajoutant le fab button de création de conversations activé. */
	private enableFabButton(): void {
		this.hideFabButtonInput.set(false);
	}

	/** Gestion du click du fab button de création de conversations. */
	public onCreateConversationClicked(): void {
		this.createConversation$().subscribe();
	}

	private createOpenConvOptions(pbIsModal: boolean = false): IOpenConversationOptions {
		const loParams: IConversationListParams | undefined | null = this.params();
		return {
			galleryAcceptFiles: loParams?.galleryAcceptFiles,
			currentContactId: this.userContactId,
			isModal: pbIsModal,
			linkedEntities: loParams?.linkedEntities,
			contactSelectorParams: loParams?.contactSelectorParams
		} as IOpenConversationOptions;
	}

	/** Ouvre les options de l'itemSliding après un swipe ou clic sur le bouton des options.
	 * @param poItemSliding Liste des items pouvant être glissés pour révéler des boutons.
	 */
	public onSlidingItemButtonOptionClicked(poItemSliding: IonItemSliding): void {
		this.openOrCloseItemSliding(poItemSliding);
	}

	private async openOrCloseItemSliding(poItemSliding: IonItemSliding): Promise<void> {
		const lsActiveItemCssClass = "item-sliding-active-slide";
		const lsAnimatingItemCssClass = "item-sliding-closing";

		if (
			(poItemSliding as IonItemSliding & { el: { classList: DOMTokenList } }).el.classList.contains(
				lsActiveItemCssClass
			)
		) {
			this.closeItemSlidingOptions();
		} else {
			this.moActiveItemSliding = poItemSliding;

			// #22287 Dans certains cas, les boutons du sliding item restent bloqués. Ionic applique une classe "item-sliding-closing" pendant l'animation d'ouverture et fermeture.
			// Cette classe bloque l'UI. Elle n'est pas toujours retirée après animation. On force le retrait de cette classe après animation.
			try {
				await poItemSliding.open("end");
			} finally {
				poItemSliding["el"].classList.remove(lsAnimatingItemCssClass);
			}
		}
	}

	/** Méthode appelée lors du clic sur l'avatar d'une conversation.
	 * @param poConversation IConversation associée à l'avatar.
	 * @param poEvent Événement du clic.
	 */
	public onAvatarClicked(poConversation: IConversationListItem, poEvent: MouseEvent): void {
		const laParticipants: IParticipant[] = this.getOtherContactParticipants(poConversation.participants);

		if (laParticipants.length === 1) {
			this.isvcPopover.showCustomPopover(
				LinkPopoverComponent,
				{
					links: this.createPopoverLinksInfo(ArrayHelper.getFirstElement(laParticipants)!)
				},
				poEvent
			);
		}
	}

	/** Récupère les participants d'une conversation, hormis l'utilisateur.
	 * @param paParticipants Liste des participants.
	 */
	private getOtherContactParticipants(paParticipants?: IParticipant<IGroupMember>[]): IParticipant[] {
		return this.isvcConversation
			.getContacts(paParticipants)
			.filter((poParticipant: IParticipant<IContact>) => poParticipant.participantId !== this.userContactId);
	}

	/** Crée les informations pour la créations des liens du menu contextuel.
	 * @param poParticipant Participant dont il faut afficher le contact.
	 */
	private createPopoverLinksInfo(poParticipant: IParticipant): [LinkInfo, LinkInfo] {
		return [this.getContactNavigationLinkInfo(poParticipant), this.getContactFilterLinkInfo(poParticipant)];
	}

	private getContactNavigationLinkInfo(poParticipant: IParticipant): LinkInfo {
		return new LinkInfo({
			label: "Voir le contact",
			action: ELinkAction.callback,
			templateId: ELinkTemplate.item,
			actionParams: {
				function: () => {
					this.isvcConversation.getContactFromParticipantAsync(poParticipant).then((poContact?: Contact) => {
						if (poContact) return this.isvcContact.routeToContact(poContact);
						else {
							console.error(
								`${ConversationsListComponent.C_LOG_ID}Error while getting contact from participant '${poParticipant.participantPath}' : it does not exist !`
							);
							this.isvcUiMessage.showPopupMessage(
								new ShowMessageParamsPopup({
									header: "Erreur",
									message: "Un problème est survenu, impossible de naviguer vers la fiche contact du participant."
								})
							);
						}
					});
				}
			}
		});
	}

	private getContactFilterLinkInfo(poParticipant: IParticipant): LinkInfo {
		return new LinkInfo({
			label: "Filtrer par ce contact",
			action: ELinkAction.callback,
			templateId: ELinkTemplate.item,
			actionParams: {
				function: () => {
					this.searchValue.set(poParticipant.label);
				}
			}
		});
	}

	/** Fonction de recherche dans une conversation.
	 * @param poConversation Conversation dans laquelle rechercher.
	 */
	private filterConversation(poConversation: IConversation): boolean {
		const lsSearchValue: string = this.searchValue();

		// Recherche dans le titre de la conversation.
		if (StringHelper.isValid(poConversation.title) && this.isSearchValueInField(poConversation.title, lsSearchValue))
			return true;

		// Recherche dans les participants.
		if (
			poConversation.participants?.some((poParticipant: IParticipant) =>
				this.isSearchValueInField(poParticipant.label, lsSearchValue)
			)
		) {
			return true;
		}

		// Recherche dans le dernier message.
		if (
			StringHelper.isValid(poConversation.lastMessage?.body) &&
			this.isSearchValueInField(poConversation.lastMessage.body, lsSearchValue)
		)
			return true;

		// Pas de correspondance trouvée.
		return false;
	}

	/** `true` si la valeur de recherche est incluse dans le champs, sinon `false`.
	 * @param psField Valeur du champs dans laquelle rechercher.
	 * @param psSearchValue Valeur à rechercher.
	 */
	private isSearchValueInField(psField: string, psSearchValue: string): boolean {
		return StringHelper.includesWithoutDiacriticCase(psField, psSearchValue);
	}

	/** Méthode appelée lors d'un changement dans les conversations filtrées. */
	public onFilteredConversationsChanged(psTitle: string = ""): void {
		this.searchValue.set(psTitle);
	}

	/** Appelle le filtre correspondant au statut.
	 * @param paItems Conversations à filtrer.
	 */
	private filterConversationByStatus(paItems: IConversationListItem[]): IConversationListItem[] {
		const loStatusFilter: EConversationStatus | undefined = this.conversationsStatusFilter();
		if (loStatusFilter === EConversationStatus.active) return this.getFilteredActiveConversations(paItems);
		else if (loStatusFilter === EConversationStatus.archived) return this.getFilteredArchivedConversations(paItems);
		else return paItems;
	}

	/** Retourne les conversations actives.
	 * @param paItems Conversations à filtrer.
	 */
	private getFilteredActiveConversations(paItems: IConversationListItem[]): IConversationListItem[] {
		return paItems.filter((poItem: IConversationListItem) => !this.isConversationArchived(poItem));
	}

	/** Retourne les conversation archivées.
	 * @param paItems Les conversation à filtrer.
	 */
	private getFilteredArchivedConversations(paItems: IConversationListItem[]): IConversationListItem[] {
		return paItems.filter((poItem: IConversationListItem) => this.isConversationArchived(poItem));
	}

	/** Retourne `true` si la conversation est archivée, sinon false.
	 * @param poItem La conversation à vérifier.
	 */
	private isConversationArchived(poItem: IConversationListItem): boolean {
		// Pour que la conversation soit considérée comme archivée, il faut que l'activité utilisateur l'indique et que la conversation soit considérée comme lue.
		if (poItem?.userActivity?.archive && this.isRead(poItem)) {
			if (!poItem.conversation.lastMessage)
				// Aucun message n'a été posté dans la conversation.
				return true;
			// L'archivage a eu lieu après le dernier message.
			else if (DateHelper.compareTwoDates(poItem.userActivity.archive, poItem.conversation.lastMessage.createDate) > 0)
				return true;
		}

		return false; // Par défaut, la conversation n'est pas archivée.
	}

	/** Retourne la liste des conversations de l'activité la plus récente à la moins récente. */
	public orderConversationsByDate(paItems: IConversationListItem[]): IConversationListItem[] {
		return paItems.sort((poItem1: IConversationListItem, poItem2: IConversationListItem) => {
			// On prend l'activité du dernier message s'il existe, sinon celle de la date de création de la conversation.
			return DateHelper.compareTwoDates(
				poItem2.conversation.lastMessage
					? poItem2.conversation.lastMessage.createDate
					: poItem2.conversation.createDate,
				poItem1.conversation.lastMessage ? poItem1.conversation.lastMessage.createDate : poItem1.conversation.createDate
			);
		});
	}

	/** Permet de récuperer la classe CSS pour l'affichage de la conversation.
	 * @param poConversation
	 */
	public isRead(poConversation: IConversationListItem): boolean {
		return this.isvcConversation.isRead(
			poConversation.conversation,
			this.userJoinDateByGroupId(),
			poConversation.userActivity
		);
	}

	/** Archive la conversation.
	 * @param poConversation La conversation à archiver.
	 */
	public onConversationArchiveClicked(poConversation: IConversation): void {
		this.archiveOrRestoreConversation$(poConversation, "archive").subscribe();
	}

	/** Restaure la conversation.
	 * @param poConversation La conversation à restaurer.
	 */
	public onConversationRestoreClicked(poConversation: IConversation): void {
		this.archiveOrRestoreConversation$(poConversation, "restore").subscribe();
	}

	private archiveOrRestoreConversation$(poConversation: IConversation, psMode: "archive" | "restore"): Observable<any> {
		const lsConversationUserActivityId: string = this.isvcConversation.getConversationUserActivityId(poConversation);

		return this.isvcConversation
			.getConversationUserActivity(lsConversationUserActivityId)
			.pipe(
				mergeMap((poConversationActivity: IConversationActivity) =>
					psMode === "archive"
						? this.isvcArchiving.archiveConversation(poConversationActivity)
						: this.isvcArchiving.restoreConversation(poConversationActivity)
				)
			);
	}

	public presentAdvancedOptions(poEvent: Event): void {
		this.isvcPopover.showPopover(this.observableAdvancedOptions, poEvent as MouseEvent);
	}

	private getAdvancedOptions(): IPopoverItemParams[] {
		const laAdvancedOptions: IPopoverItemParams[] = [];
		if (this.canInteropMailbox) {
			laAdvancedOptions.push({
				color: "primary",
				icon: "download",
				title: "Connecter CalaoTrade à ...",
				action: () => this.openInteropModal$()
			});
		}
		return laAdvancedOptions;
	}

	private openInteropModal$(): Observable<any> {
		return this.isvcModal.open(
			{
				component: InteropModalComponent,
				componentProps: {},
				cssClass: "transparent"
			},
			EModalSize.medium
		);
	}

	private initConversationTypeEnabled(): void {
		this.conversationTypeEnabled = [EConversationType.chat];
		if (
			this.items().some((poItem: IConversationListItem) =>
				poItem.conversation.conversationType ? poItem.conversation.conversationType === EConversationType.mail : false
			)
		) {
			this.conversationTypeEnabled.push(EConversationType.mail);
		}
		this.conversationTypeFilteredSignal.set(this.conversationTypeEnabled);
	}

	public switchConversationTypeEnabled(psConversationType: EConversationType): void {
		if (this.conversationTypeFilteredSignal().includes(psConversationType)) {
			if (this.conversationTypeFilteredSignal.length !== 1) {
				this.conversationTypeFilteredSignal.set(
					this.conversationTypeFilteredSignal().filter(
						(psConversationTypeInArray: string) => psConversationTypeInArray !== psConversationType
					)
				);
			}
		} else {
			this.conversationTypeFilteredSignal.set([...this.conversationTypeFilteredSignal(), psConversationType]);
		}
	}

	public filterConversationType(paConversationType: IConversationListItem[]): IConversationListItem[] {
		return paConversationType.filter((poConversation: IConversationListItem) =>
			this.isConversationFilteredByConversationType(poConversation)
		);
	}

	private isConversationFilteredByConversationType(poItem: IConversationListItem): boolean {
		return this.conversationTypeFilteredSignal().includes(
			poItem.conversation.conversationType ?? EConversationType.chat
		);
	}

	public trackByConversationRev(pnIndex: number, poItem: IConversationListItem): string {
		return (poItem.conversation._rev ?? "") + (poItem.userActivity?._rev ?? "");
	}

	//#endregion
}
